/**
 * 批量任务API客户端
 * 支持智能延迟解析的资源化API架构
 *
 * 使用 DTO + Mapper 模式：
 * - API 返回 DTO 类型
 * - 通过 Mapper 转换为域模型
 * - 组件只使用域模型
 *
 * 架构设计：
 * - 传输层：http.post() 返回 AxiosResponse<ApiResponse<T>>
 * - SDK层：unwrap() 自动解包 ApiResponse，返回 T
 * - 业务层：直接使用干净的 DTO 类型
 */

import { http } from '@/utils/http'
import { unwrap } from '@/utils/apiClient'
import type {
  UploadFileDTO,
  BatchFileDTO,
  StartParseDTO,
  ParseStatusDTO,
  BatchSessionDTO,
  SessionListDTO,
  CreateBundleDTO,
  BundleStatusDTO,
  UserBundlesDTO,
  CreateSessionDTO,
  FileContentDTO
} from '@/types/batch.dto'

// ============= 请求类型 =============

export interface StartParseRequest {
  // V1.0简化版：不需要分段参数
}

export interface CreateBundleRequest {
  sessionUid: string
  format?: string
}

export interface CreateSessionRequest {
  fileUids: string[]
  title: string
  description: string
  voice: string
  audioFormat: string
  rate: number
  pitch: number
  style: string
}

// ============= API客户端类 =============

class BatchAPI {
  private baseURL = '/api/v1/batch'

  // ============= 文件管理 =============

  /**
   * 上传单个文件
   * 使用 unwrap() 自动解包 ApiResponse，返回干净的 DTO
   */
  async uploadFile(file: File): Promise<UploadFileDTO> {
    const formData = new FormData()
    formData.append('file', file)

    return unwrap<UploadFileDTO>(() =>
      http.post(`${this.baseURL}/files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        transform: {
          disableDedupe: true  // 禁用请求去重，允许同时上传多个文件
        }
      })
    )
  }

  /**
   * 获取文件信息
   */
  async getFileInfo(fileUid: string): Promise<BatchFileDTO> {
    return unwrap<BatchFileDTO>(() =>
      http.get(`${this.baseURL}/files/${fileUid}`)
    )
  }

  /**
   * 获取文件完整内容（用于前端展示原文/清洗后文本）
   */
  async getFileContent(fileUid: string): Promise<FileContentDTO> {
    return unwrap<FileContentDTO>(() =>
      http.get(`${this.baseURL}/files/${fileUid}/content`)
    )
  }

  /**
   * 删除文件
   */
  async deleteFile(fileUid: string): Promise<void> {
    return unwrap<void>(() =>
      http.delete(`${this.baseURL}/files/${fileUid}`)
    )
  }

  // ============= 解析管理 =============

  /**
   * 开始解析文件
   */
  async startParse(fileUid: string, options: StartParseRequest = {}): Promise<StartParseDTO> {
    return unwrap<StartParseDTO>(() =>
      http.post(`${this.baseURL}/files/${fileUid}/parse`, options)
    )
  }

  /**
   * 批量获取解析状态
   * @param fileUids 文件UID列表
   */
  async getBatchParseStatus(fileUids: string[]): Promise<ParseStatusDTO[]> {
    if (fileUids.length === 0) {
      return []
    }
    return unwrap<ParseStatusDTO[]>(() =>
      http.get(`${this.baseURL}/files/parse-status`, {
        params: { fileUids: fileUids.join(',') }
      })
    )
  }

  // ============= Bundle 打包管理 =============

  /**
   * 创建批次打包下载
   */
  async createSessionBundle(sessionUid: string, format = 'zip'): Promise<CreateBundleDTO> {
    return unwrap<CreateBundleDTO>(() =>
      http.post(`${this.baseURL}/sessions/${sessionUid}/bundle`, {
        sessionUid,
        format
      })
    )
  }

  /**
   * 获取打包状态
   */
  async getBundleStatus(bundleId: string): Promise<BundleStatusDTO> {
    return unwrap<BundleStatusDTO>(() =>
      http.get(`${this.baseURL}/bundle-status`, {
        params: { id: bundleId }
      })
    )
  }

  /**
   * 获取用户打包列表
   */
  async getUserBundles(page = 1, pageSize = 20): Promise<UserBundlesDTO> {
    return unwrap<UserBundlesDTO>(() =>
      http.get(`${this.baseURL}/bundles`, {
        params: { page, pageSize }
      })
    )
  }

  // ============= 会话管理 =============

  /**
   * 创建会话（模式 A：创建即提交）
   * 后端在创建时自动创建 WorkItems 并设置为 ready 状态
   */
  async createSession(data: CreateSessionRequest): Promise<CreateSessionDTO> {
    return unwrap<CreateSessionDTO>(() =>
      http.post(`${this.baseURL}/sessions`, data)
    )
  }

  /**
   * 获取会话状态（包含批量文件）
   */
  async getSessionStatus(sessionUid: string, includeFiles = true): Promise<BatchSessionDTO> {
    return unwrap<BatchSessionDTO>(() =>
      http.get(`${this.baseURL}/sessions/${sessionUid}/status`, {
        params: { includeFiles }
      })
    )
  }

  /**
   * 批量获取会话状态
   * @param sessionUids 会话UID列表
   * @param includeFiles 是否包含文件信息
   */
  async getBatchSessionsStatus(sessionUids: string[], includeFiles = true): Promise<Record<string, BatchSessionDTO>> {
    if (sessionUids.length === 0) {
      return {}
    }
    return unwrap<Record<string, BatchSessionDTO>>(() =>
      http.post(`${this.baseURL}/sessions/batch-status`, {
        sessionUids,
        includeFiles
      })
    )
  }

  /**
   * 取消会话
   */
  async cancelSession(sessionUid: string): Promise<void> {
    return unwrap<void>(() =>
      http.post(`${this.baseURL}/sessions/${sessionUid}/cancel`)
    )
  }

  /**
   * 获取最近的会话列表
   */
  async getRecentSessions(page = 1, limit = 20): Promise<SessionListDTO> {
    return unwrap<SessionListDTO>(() =>
      http.get(`${this.baseURL}/sessions`, {
        params: { page, limit }
      })
    )
  }

  /**
   * 获取进行中的会话状态（轻量级）
   * 只返回进行中的会话，不包含文件列表
   */
  async getActiveSessionsStatus(): Promise<{ sessions: Array<{
    sessionUid: string
    phase: string
    progress: number
    totalFiles: number
    completedFiles: number
    failedFiles: number
    updatedAt: string
  }> }> {
    return unwrap(() =>
      http.get(`${this.baseURL}/sessions/status/active`)
    )
  }

  // ============= WorkItem 下载管理 =============

  /**
   * 生成WorkItem预签名下载链接
   * @param workItemUid WorkItem UID
   * @param ttlHours 链接有效期（小时），默认24小时，最大168小时（7天）
   */
  async generateWorkItemDownloadURL(workItemUid: string, ttlHours?: number): Promise<{
    downloadUrl: string
    ttlHours: number
    expiresAt: number
  }> {
    const params = ttlHours ? { ttlHours } : {}
    return unwrap(() =>
      http.post(`${this.baseURL}/work-items/${workItemUid}/download-url`, {}, { params })
    )
  }
}

// 导出单例
export const batchAPI = new BatchAPI()
export default batchAPI
